package fr.frux.core.maps

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.painter.Painter
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.DpSize
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.em
import androidx.compose.ui.unit.sp
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.longOrNull
import kotlinx.serialization.json.put
import org.maplibre.compose.expressions.ast.Expression
import org.maplibre.compose.expressions.dsl.Feature
import org.maplibre.compose.expressions.dsl.asBoolean
import org.maplibre.compose.expressions.dsl.asString
import org.maplibre.compose.expressions.dsl.case
import org.maplibre.compose.expressions.dsl.condition
import org.maplibre.compose.expressions.dsl.const
import org.maplibre.compose.expressions.dsl.eq
import org.maplibre.compose.expressions.dsl.image
import org.maplibre.compose.expressions.dsl.padding
import org.maplibre.compose.expressions.dsl.switch
import org.maplibre.compose.expressions.dsl.textOffset
import org.maplibre.compose.expressions.value.BooleanValue
import org.maplibre.compose.expressions.value.IconTextFit
import org.maplibre.compose.interaction.ClickResult
import org.maplibre.compose.layers.FeaturesClickHandler
import org.maplibre.compose.layers.SymbolLayer
import org.maplibre.compose.sources.GeoJsonData
import org.maplibre.compose.sources.GeoJsonSource
import org.maplibre.compose.sources.rememberGeoJsonSource
import org.maplibre.compose.util.ImageStretch
import org.maplibre.spatialk.geojson.FeatureCollection
import org.maplibre.spatialk.geojson.Point
import org.maplibre.spatialk.geojson.Feature as GeoJsonFeature

private const val PLAQUE_ID_PROPERTY = "plaqueId"
private const val PLAQUE_STYLE_PROPERTY = "style"
private const val PLAQUE_BADGE_PROPERTY = "badge"
private const val PLAQUE_BADGE_TONE_PROPERTY = "badgeTone"
private const val BADGE_TONE_DARK = "dark"
private const val BADGE_TONE_ACCENT = "accent"
private const val PLAQUE_SELECTED_PROPERTY = "selected"

private const val SITES_LAYER_ID = "frux-sites"
private const val SITES_SELECTED_LAYER_ID = "frux-sites-selected"
private const val SITES_BADGE_LAYER_ID = "frux-sites-badge"

private const val BADGE_TEXT_SIZE_SP = 12f

/** Blanc franc du texte de badge, sur la pastille sombre. */
private val PLAQUE_BADGE_TEXT_COLOR = Color.White
private val BADGE_TEXT_PADDING_VERTICAL: Dp = 2.dp
private val BADGE_TEXT_PADDING_HORIZONTAL: Dp = 6.dp

/** Decalage du badge vers le coin haut droit d'une plaque de cote [side], en em du texte. */
private fun badgeOffsetEm(side: Dp): Float = side.value / 2f / BADGE_TEXT_SIZE_SP

/**
 * Couches des plaques de lieu, posees au-dessus des arbres et de leurs regroupements.
 *
 * Source sans regroupement : les lieux sont peu nombreux et une plaque comptee parmi des arbres
 * perdrait ce qui la distingue. La selection passe par une seconde couche filtree plutot que par
 * une condition sur `iconImage` (crash iOS, maplibre-compose #310), chacune avec ses rasters a la
 * taille ou ils s'affichent. Le badge est une troisieme couche de texte, une seule pour tous les
 * nombres : la pastille s'etire autour du texte (`iconTextFit`), sans un raster par valeur.
 */
@Composable
internal fun SitePlaqueLayers(
    plaques: List<MapPlaque>,
    markerIcons: Map<String, Painter>,
    fallbackGlyph: Painter?,
    onPlaqueClick: (PlaqueId) -> Unit,
    onUserGesture: () -> Unit,
) {
    val currentOnPlaqueClick by rememberUpdatedState(onPlaqueClick)
    val currentOnUserGesture by rememberUpdatedState(onUserGesture)

    val source =
        rememberGeoJsonSource(
            data = GeoJsonData.Features(remember(plaques) { plaques.toPlaqueFeatureCollection() }),
        )
    if (plaques.isEmpty()) return

    // Painters d'identite stable : un painter recree ferait re-dessiner et comparer chaque sprite a
    // chaque recomposition.
    val compositeCache = remember(markerIcons, fallbackGlyph) { mutableMapOf<String, Painter>() }
    val plaqueImages =
        remember(plaques, compositeCache) {
            plaques.distinctBy(::plaqueStyleKey).associate { plaque ->
                val key = plaqueStyleKey(plaque)
                key to
                    compositeCache.getOrPut(key) {
                        CompositePlaquePainter(
                            rimColor = markerRimColor(plaque.soon),
                            plaqueColor = plaque.tint,
                            glyph = plaque.iconKey?.let { markerIcons[it] ?: fallbackGlyph },
                            glyphColor = GLYPH_COLOR,
                        )
                    }
            }
        }
    val selectedKeys = remember(plaques) { selectedPlaqueStyleKeys(plaques) }

    fun imagesBySize(
        keys: Collection<String>,
        side: Dp,
    ) = switch(
        input = Feature[PLAQUE_STYLE_PROPERTY].asString(const(DEFAULT_STYLE_KEY)),
        *keys.map { key -> case(key, image(plaqueImages.getValue(key), size = DpSize(side, side))) }.toTypedArray(),
        // Jamais atteint : les cas couvrent par construction toutes les cles presentes dans
        // `plaques`. Une plaque existante en garde-fou plutot qu'un trou sur la carte.
        fallback = image(plaqueImages.values.first(), size = DpSize(side, side)),
    )

    val onClick =
        plaqueClickHandler(
            onUserGesture = { currentOnUserGesture() },
            onPlaqueClick = { currentOnPlaqueClick(it) },
        )

    val isSelected = Feature[PLAQUE_SELECTED_PROPERTY].asBoolean(const(false))
    SymbolLayer(
        id = SITES_LAYER_ID,
        source = source,
        filter = isSelected.eq(const(false)),
        iconImage = imagesBySize(plaqueImages.keys, COMPOSITE_MARKER_SIZE),
        iconAllowOverlap = const(true),
        onClick = onClick,
    )
    if (selectedKeys.isNotEmpty()) {
        SymbolLayer(
            id = SITES_SELECTED_LAYER_ID,
            source = source,
            filter = isSelected,
            iconImage = imagesBySize(selectedKeys, SELECTED_COMPOSITE_MARKER_SIZE),
            iconAllowOverlap = const(true),
            onClick = onClick,
        )
    }

    SitePlaqueBadgeLayer(source = source, isSelected = isSelected, onClick = onClick)
}

/**
 * Clic sur une plaque ou sur son badge. Le badge deborde de la plaque : sans ce meme gestionnaire
 * sur sa couche, un tap sur sa moitie exterieure passait a la carte.
 */
internal fun plaqueClickHandler(
    onUserGesture: () -> Unit,
    onPlaqueClick: (PlaqueId) -> Unit,
): FeaturesClickHandler =
    { features ->
        val clicked =
            features.firstNotNullOfOrNull { feature ->
                feature.properties
                    ?.get(PLAQUE_ID_PROPERTY)
                    ?.jsonPrimitive
                    ?.longOrNull
                    ?.let(::PlaqueId)
            }
        if (clicked == null) {
            ClickResult.Pass
        } else {
            onUserGesture()
            onPlaqueClick(clicked)
            ClickResult.Consume
        }
    }

/**
 * Pastille de badge au coin haut droit de chaque plaque qui en porte un. Une seule couche de texte
 * pour tous les nombres : la pastille s'etire autour du texte (`iconTextFit`), sans raster par
 * valeur.
 */
@Composable
private fun SitePlaqueBadgeLayer(
    source: GeoJsonSource,
    isSelected: Expression<BooleanValue>,
    onClick: FeaturesClickHandler,
) {
    // Deux painters d'identite stable, choisis par feature : un `switch` sur la teinte, jamais une `condition`.
    val darkPill = remember { PlaqueBadgePillPainter(PLAQUE_BADGE_COLOR) }
    val accentPill = remember { PlaqueBadgePillPainter(PLAQUE_BADGE_ACCENT_COLOR) }

    fun pill(painter: Painter) =
        image(
            painter,
            size = DpSize(PLAQUE_BADGE_PILL_SIZE, PLAQUE_BADGE_PILL_SIZE),
            stretch =
                ImageStretch(
                    x = listOf(PLAQUE_BADGE_STRETCH_START..PLAQUE_BADGE_STRETCH_END),
                    y = listOf(PLAQUE_BADGE_STRETCH_START..PLAQUE_BADGE_STRETCH_END),
                ),
        )

    fun badgeOffset(side: Dp) =
        textOffset(badgeOffsetEm(side).em, (CLUSTER_TEXT_BASELINE_SHIFT_EM - badgeOffsetEm(side)).em)
    SymbolLayer(
        id = SITES_BADGE_LAYER_ID,
        source = source,
        filter = Feature.has(PLAQUE_BADGE_PROPERTY),
        textField = Feature[PLAQUE_BADGE_PROPERTY].asString(const("")),
        textFont = const(listOf(CLUSTER_FONT)),
        textColor = const(PLAQUE_BADGE_TEXT_COLOR),
        textSize = const(BADGE_TEXT_SIZE_SP.sp),
        // Condition sur la position du texte, pas sur `iconImage` : la regle iOS ne vise que l'image.
        textOffset =
            switch(
                condition(test = isSelected, output = badgeOffset(SELECTED_COMPOSITE_MARKER_SIZE)),
                fallback = badgeOffset(COMPOSITE_MARKER_SIZE),
            ),
        textAllowOverlap = const(true),
        iconImage =
            switch(
                input = Feature[PLAQUE_BADGE_TONE_PROPERTY].asString(const(BADGE_TONE_DARK)),
                case(BADGE_TONE_ACCENT, pill(accentPill)),
                fallback = pill(darkPill),
            ),
        iconTextFit = const(IconTextFit.Both),
        iconTextFitPadding =
            padding(
                left = BADGE_TEXT_PADDING_HORIZONTAL,
                top = BADGE_TEXT_PADDING_VERTICAL,
                right = BADGE_TEXT_PADDING_HORIZONTAL,
                bottom = BADGE_TEXT_PADDING_VERTICAL,
            ),
        iconAllowOverlap = const(true),
        onClick = onClick,
    )
}

private fun badgeTone(plaque: MapPlaque): String = if (plaque.badgeAccent) BADGE_TONE_ACCENT else BADGE_TONE_DARK

internal fun List<MapPlaque>.toPlaqueFeatureCollection(): FeatureCollection<Point, JsonObject?> =
    FeatureCollection(
        features =
            map { plaque ->
                GeoJsonFeature(
                    geometry = Point(plaque.position.toPosition()),
                    properties =
                        buildJsonObject {
                            put(PLAQUE_ID_PROPERTY, plaque.id.value)
                            put(PLAQUE_STYLE_PROPERTY, plaqueStyleKey(plaque))
                            plaque.badge?.let { put(PLAQUE_BADGE_PROPERTY, it) }
                            put(PLAQUE_BADGE_TONE_PROPERTY, badgeTone(plaque))
                            put(PLAQUE_SELECTED_PROPERTY, plaque.selected)
                        },
                )
            },
    )
